import { Dispatch, FC, SetStateAction, useEffect, useMemo, useState } from 'react';
import { generalHelpers } from '@monorepo/helpers';
import { hideLoadingModalReducer, setAlertResponse, showLoadingModalReducer } from 'src/store';
import { meetingServices } from 'src/services';
import { Modal } from 'react-bootstrap';
import { typeMeeting } from '@monorepo/models';
import { useDispatch } from 'react-redux';
import { useGetIsDarkMode } from 'src/custom-hooks';

type propsType = {
    closeModal: () => void;
    meetingToEdit: typeMeeting | null;
    meetings: typeMeeting[];
    setRefreshCounter: Dispatch<SetStateAction<number>>;
};

export const MeetingAttendanceCrudModal: FC<propsType> = ({ closeModal, meetings, meetingToEdit, setRefreshCounter }) => {
    const [inPerson, setInPerson] = useState(meetingToEdit?.attendance.inPerson ?? 0);
    const [notes, setNotes] = useState(meetingToEdit?.attendance.notes ?? '');
    const [youtube, setYoutube] = useState(meetingToEdit?.attendance.youtube ?? 0);
    const [zoom, setZoom] = useState(meetingToEdit?.attendance.zoom ?? 0);
    //
    const [date, setDate] = useState(meetingToEdit?.date ?? '');
    const [isMidweek, setisMidweek] = useState(false);
    const [weekday, setWeekday] = useState(meetingToEdit?.weekday ?? 0);
    const [weekNumber, setWeekNumber] = useState(meetingToEdit?.weekNumber ?? 0);
    const [year, setYear] = useState(meetingToEdit?.year ?? 0);
    const dispatch = useDispatch();
    const isDarkMode = useGetIsDarkMode();

    useEffect(() => {
        const _date = generalHelpers.GetDateFromDateString(date);
        setWeekNumber(generalHelpers.GetCalendarWeekNumber(_date));
        setYear(parseInt(date.split('-')[0]));
        const _weekday = _date.getDay();
        setWeekday(_weekday)
        setisMidweek(!!_weekday && _weekday <= 5);
    }, [date, meetingToEdit]);

    const isValid: boolean = useMemo(() => {
        if (!date) {
            return false;
        }
        const existingMeeting = meetings.find(m =>
            m.year === year
            && m.weekNumber === weekNumber
            && ((isMidweek && m.weekday > 0 && m.weekday <= 5) || (!isMidweek && (m.weekday === 0 || m.weekday === 6)))
        );
        if (existingMeeting) {
            if (meetingToEdit) {
                if (existingMeeting.id !== meetingToEdit.id) {
                    return false;
                }
            } else {
                return false;
            }
        }
        return true;
    }, [date, isMidweek, meetingToEdit, meetings, weekday, weekNumber, year]);

    const saveHandler = async () => {
        if (!isValid) {
            return;
        }
        dispatch(showLoadingModalReducer());
        const m: typeMeeting = meetingToEdit ?
            {
                ...meetingToEdit,
                attendance: {
                    inPerson,
                    notes,
                    youtube,
                    zoom
                },
                date,
                type: isMidweek ? 'midweek' : 'public',
                weekday,
                weekNumber,
                year
            }
            :
            {
                attendance: {
                    inPerson,
                    notes,
                    youtube,
                    zoom
                },
                avAssignments: [],
                date,
                groupsForCleaning: [],
                id: 0,
                sections: [],
                type: isMidweek ? 'midweek' : 'public',
                usherAssignments: [],
                weekday,
                weekNumber,
                year
            }
        ;
        let response = await meetingServices.SaveAttendance(m);
        dispatch(hideLoadingModalReducer());
        if (!response.success) {
            dispatch(setAlertResponse({ response }));
            return;
        }
        setRefreshCounter(x => x + 1);
        closeModal();
    }

    return (
        <Modal show size={'xl'} onHide={closeModal}>
            <Modal.Header className={isDarkMode ? 'bg-dark text-light' : ''} closeButton>
                <h3 className={'mb-0'}>
                    {meetingToEdit ? `EDITAR ASISTENCIA DEL ${meetingToEdit.date}` : 'AGREGAR REUNIÓN'}
                </h3>
            </Modal.Header>
            <Modal.Body className={isDarkMode ? 'bg-lightdark text-light' : ''}>
                <div className={`card ${isDarkMode ? 'bg-lightdark text-white' : ''} mx-auto w-100 p-3 mb-3`}>
                    <div className={'d-flex flex-lg-row flex-column gap-3 mb-4'}>
                        <div className={'row justify-content-center'} style={{ width: '250px' }}>
                            <div>
                                <i className={'fas fa-calendar-alt fa-lg me-2'} />
                                <label htmlFor={'mDate'} className={'form-label fw-bold'}>
                                    Fecha
                                </label>
                                <input type={'date'}
                                    id={'mDate'}
                                    className={'form-control'}
                                    value={date}
                                    onChange={e => setDate(e.target.value)}
                                    disabled={!!meetingToEdit?.sections.length}
                                />
                            </div>
                        </div>
                        <div className={'row justify-content-center'} style={{ width: '250px' }}>
                            <div>
                                <i className={'fa-solid fa-users fa-lg me-2'} />
                                <label htmlFor={'inPerson'} className={'form-label fw-bold'}>
                                    Presencial
                                </label>
                                <input type={'number'}
                                    id={'inPerson'}
                                    className={'form-control'}
                                    placeholder={'0'}
                                    value={inPerson || ''}
                                    onChange={e => {
                                        const c = parseInt(e.target.value) || 0;
                                        if (c < 0 || c > 9999) {
                                            return;
                                        }
                                        setInPerson(c);
                                    }}
                                />
                            </div>
                        </div>
                        <div className={'row justify-content-center'} style={{ width: '250px' }}>
                            <div>
                                <i className={'fas fa-video fa-lg me-2'} />
                                <label htmlFor={'zoom'} className={'form-label fw-bold'}>
                                    Zoom
                                </label>
                                <input type={'number'}
                                    id={'zoom'}
                                    className={'form-control'}
                                    placeholder={'0'}
                                    value={zoom || ''}
                                    onChange={e => {
                                        const c = parseInt(e.target.value) || 0;
                                        if (c < 0 || c > 9999) {
                                            return
                                        }
                                        setZoom(c);
                                    }}
                                />
                            </div>
                        </div>
                        <div className={'row justify-content-center'} style={{ width: '250px' }}>
                            <div>
                                <i className={'fab fa-youtube fa-lg me-2'} />
                                <label htmlFor={'youtube'} className={'form-label fw-bold'}>
                                    YouTube
                                </label>
                                <input type={'number'}
                                    id={'youtube'}
                                    className={'form-control'}
                                    placeholder={'0'}
                                    value={youtube || ''}
                                    onChange={e => {
                                        const c = parseInt(e.target.value) || 0;
                                        if (c < 0 || c > 9999) {
                                            return;
                                        }
                                        setYoutube(c);
                                    }}
                                    disabled
                                />
                            </div>
                        </div>
                        <div className={'row justify-content-center'} style={{ width: '290px' }}>
                            <div>
                                <label htmlFor={'total'} className={'form-label fw-bold'}>
                                    Total
                                </label>
                                <input type={'number'}
                                    id={'total'}
                                    className={'form-control'}
                                    placeholder={'0'}
                                    value={inPerson + zoom + youtube}
                                    disabled
                                />
                            </div>
                        </div>
                        <div className={'row justify-content-center w-100'}>
                            <div>
                                <i className={'fas fa-sticky-note fa-lg me-2'} />
                                <label htmlFor={'notas'} className={'form-label fw-bold'}>
                                    Notas
                                </label>
                                <input type={'text'}
                                    className={'form-control'}
                                    value={notes}
                                    onChange={e => e.target.value.length < 90 ?
                                        setNotes(e.target.value)
                                        :
                                        null
                                    }
                                />
                            </div>
                        </div>
                    </div>
                    {!!meetingToEdit?.sections.length &&
                        <div className={'alert alert-warning'} role={'alert'}>
                            Si se necesita editar la fecha de esta reunión, hay que hacerlo desde la sección de reuniones {meetingToEdit.type === 'midweek' ? 'de entresemana' : 'públicas'}.
                        </div>
                    }
                    {!!date && !isValid &&
                        <div className={'alert alert-danger'} role={'alert'}>
                            Ya existe una reunión {isMidweek ? 'de entresemana' : 'pública'} la semana {weekNumber} de {year} y no se puede crear otra
                        </div>
                    }
                </div>
            </Modal.Body>
            <Modal.Footer className={isDarkMode ? 'bg-dark text-light' : ''}>
                <div className={'text-center'}>
                    <button className={'btn btn-general-blue px-4 me-3'}
                        style={{ width: '140px' }}
                        onClick={saveHandler}
                        disabled={!isValid}
                    >
                        GUARDAR
                    </button>
                    <button className={'btn btn-general-red px-4'}
                        style={{ width: '140px' }}
                        onClick={closeModal}
                    >
                        CANCELAR
                    </button>
                </div>
            </Modal.Footer>
        </Modal>
    );
}
